import React, {useState, useEffect, useMemo} from 'react'
import DataTable, {
  Column,
} from '../../../../Components/shared/data-table/src/app/Components/shared/data-table/table'
import {
  GridSortModel,
  GridSortItem,
} from '../../../../Components/shared/data-table/src/app/Components/shared/data-table/pagination.types'
import moment from 'moment'
import {Badge} from 'react-bootstrap'
import {
  EVENT_NAMES_MAP,
  IFlowLog,
  LOGS_EVENT_TYPE,
  LOGS_STATUS,
} from '../../../../Utils/Constant/FlowConstants'
import {useI18n} from '../../../../Utils/_helpers/useI18n'
import {useNavigate} from 'react-router-dom'

import {toast} from 'react-toastify'
import {useGetFlowLogsQuery} from '../../../../../services/cellcast.v2'

export const typeColor = (type: string) => {
  switch (type) {
    case LOGS_EVENT_TYPE.TRIGGER_COMPLETED:
    case LOGS_EVENT_TYPE.END_COMPLETED:
    case LOGS_EVENT_TYPE.DELAY_COMPLETED:
    case LOGS_EVENT_TYPE.CONDITION_DELIVERED:
    case LOGS_EVENT_TYPE.SMS_SENT:
    case LOGS_EVENT_TYPE.CONDITION_FAILED:
    case LOGS_EVENT_TYPE.SMS_DELIVERED:
      return 'success'

    case LOGS_EVENT_TYPE.DELAY_STARTED:
    case LOGS_EVENT_TYPE.CONDITION_SENT:
    case LOGS_EVENT_TYPE.CONDITION_REPLY_CONTAIN:
    case LOGS_EVENT_TYPE.CONDITION_REPLY_START_WITH:
    case LOGS_EVENT_TYPE.CONDITION_REPLY_END_WITH:
    case LOGS_EVENT_TYPE.CONDITION_EQUAL:
    case LOGS_EVENT_TYPE.SMS_REPLIED:
    case LOGS_EVENT_TYPE.SMS_REPLIED_CONTAIN:
    case LOGS_EVENT_TYPE.SMS_REPLIED_START_WITH:
    case LOGS_EVENT_TYPE.SMS_REPLIED_END_WITH:
    case LOGS_EVENT_TYPE.SMS_REPLIED_EQUAL:
      return 'warning'

    case LOGS_EVENT_TYPE.SMS_FAILED:
    case LOGS_EVENT_TYPE.INSUFFICIENT_CREDIT:
      return 'danger'

    default:
      return 'secondary'
  }
}

const statusBadgeClass = (status: string) => {
  switch ((status || '').toLowerCase()) {
    case LOGS_STATUS.COMPLETED:
      return 'badge badge-light-success fw-bold'
    case LOGS_STATUS.PENDING:
    case LOGS_STATUS.WAITING:
      return 'badge badge-light-warning fw-bold'
    case LOGS_STATUS.RUNNING:
      return 'badge badge-light-info fw-bold'
    case LOGS_STATUS.FAILED:
    case LOGS_STATUS.CANCELLED:
      return 'badge badge-light-danger fw-bold'
    default:
      return 'badge badge-light-secondary fw-bold'
  }
}

const renderTextEventType = (type: string) => {
  return EVENT_NAMES_MAP[type as keyof typeof EVENT_NAMES_MAP] || type
}

const FlowLogs = () => {
  const {t} = useI18n()
  const navigate = useNavigate()
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(10)
  const [sortData, setSortData] = useState<GridSortModel>({field: 'createdAt', sort: 'desc'})

  const {
    data: flowLogsResponse,
    isLoading,
    error,
    refetch,
  } = useGetFlowLogsQuery(
    {
      page: page + 1,
      limit: pageSize,
      field: sortData.field,
      sort: sortData.sort ?? 'desc',
    },
    {
      refetchOnMountOrArgChange: true,
    }
  )

  useEffect(() => {
    if (error) {
      toast.error(t('FLOW.LOGS.FETCH_ERROR') || 'Failed to fetch flow logs')
    }
  }, [error, t])

  useEffect(() => {
    refetch()
  }, [sortData, refetch])

  const flowLogs = useMemo(() => flowLogsResponse?.data?.items || [], [flowLogsResponse])
  const totalData = flowLogsResponse?.data?.total ?? 0
  // Reset to page 0 if current page is empty and not the first page
  useEffect(() => {
    if (!isLoading && flowLogs.length === 0 && page > 0) {
      setPage(0)
    }
  }, [flowLogs, isLoading, page])

  const navigateToJob = (jobId: string) => {
    navigate(`/automation/contacts-in-flows/event-logs/${jobId}`)
  }

  const logsWithId: IFlowLog[] = (Array.isArray(flowLogs) ? flowLogs : []).map(
    (log: IFlowLog, idx: number) => ({
      ...log,
      id: log?._id ?? idx.toString(),
    })
  )

  const columns: Column<IFlowLog>[] = [
    {
      id: 'type',
      label: t('FLOW.LOGS.TYPE'),
      sortable: true,
      width: 120,
      Cell: (row) => (
        <Badge bg={typeColor(row.type || '')} style={{fontSize: 13}}>
          {renderTextEventType(row.type || '')}
        </Badge>
      ),
    },
    {
      id: 'triggerEvent',
      label: t('FLOW.LOGS.TRIGGER'),
      sortable: false,
      width: 120,
      Cell: (row) => <span>{row.triggerEvent?.type}</span>,
    },
    {
      id: 'flowName',
      label: t('FLOW.LOGS.FLOW_NAME'),
      sortable: false,
      width: 80,
      Cell: (row) => <span>{row?.flowName}</span>,
    },
    {
      id: 'jobId',
      label: t('FLOW.LOGS.JOB_ID'),
      sortable: false,
      width: 120,
      Cell: (row) => (
        <span style={{cursor: 'pointer', display: 'inline-flex', alignItems: 'center'}}>
          {row?.instanceId}
        </span>
      ),
    },
    {
      id: 'message',
      label: t('FLOW.LOGS.MESSAGE'),
      width: 250,
      className: 'text-start',
      Cell: (row) => (
        <span style={{whiteSpace: 'pre-line', wordBreak: 'break-word'}}>{row.message}</span>
      ),
    },
    {
      id: 'status',
      label: t('FLOW.LOGS.STATUS'),
      sortable: false,
      width: 100,
      Cell: (row) => (
        <span className={statusBadgeClass(row.status || '')}>
          {row.status?.charAt(0).toUpperCase() + (row.status?.slice(1) || '')}
        </span>
      ),
    },
    {
      id: 'timestamp',
      label: t('FLOW.LOGS.RUN_AT'),
      sortable: true,
      width: 120,
      Cell: (row) => <span>{moment(row.timestamp).local().format('MMM DD YYYY HH:mm:ss')}</span>,
    },
    {
      id: 'createdAt',
      label: t('FLOW.LOGS.CREATED_AT'),
      sortable: true,
      width: 170,
      Cell: (row) => <span>{moment(row.createdAt).local().format('MMM DD YYYY HH:mm:ss')}</span>,
    },
  ]

  const handlePageChange = (newPage: number) => {
    setPage(newPage)
  }

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize)
    setPage(0)
  }

  const handleSortChange = (sortData: GridSortItem) => {
    setSortData({field: sortData.field, sort: sortData.sort ?? 'desc'})
  }

  return (
    <div className='card'>
      <div className='shadow-sm'>
        <div className='card-header border-0 p-0 align-items-center'>
          <div className='d-flex flex-column w-100'>
            <h3 className='card-title align-items-start flex-column'>
              <div className='d-flex align-items-center gap-3 mb-2'>
                <span
                  className='me-2'
                  style={{cursor: 'pointer'}}
                  onClick={() => navigate('/automation')}
                  title={t('COMMON.ACTIONS.BACK')}
                >
                  <i className='bi bi-arrow-left fs-4 text-primary'></i>
                </span>
                <span className='card-label fw-bolder fs-2 mb-1'>{t('FLOW.LOGS.TITLE')}</span>
              </div>
              <span className='text-muted mt-1 fw-bold fs-7'>{t('FLOW.LOGS.SUBTITLE')}</span>
            </h3>
          </div>
        </div>
        <div className='card-body p-0 pt-1'>
          <DataTable
            items={logsWithId}
            isLoading={isLoading}
            columns={columns}
            sortData={sortData}
            handleSortedChange={handleSortChange}
            pageSize={pageSize}
            totalData={totalData}
            handleChangePage={handlePageChange}
            handleChangSizeData={handlePageSizeChange}
            page={page}
            onRowClick={(item) => navigateToJob(item.instanceId || '')}
            onRowHover={(item, isHovering) => {}}
          />
        </div>
      </div>
    </div>
  )
}

export default FlowLogs
